import {
  CalendarIcon,
  ChartBarIcon,
  ClipboardListIcon,
  CodeIcon,
  CursorClickIcon,
  IdentificationIcon,
  MailIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import type { ComponentType, SVGProps } from 'react';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { CheckGlyph, delay, SectionHeading, Stage, tone } from './primitives';
import { useReveal } from './useReveal';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/** The demo business in this section: a hair salon with its own colour. */
const SALON_COLOR = 'bg-[#0f6b66]';

export default function AgentActions() {
  const { t } = useLocale('chatbots');
  const { ref, state } = useReveal<HTMLDivElement>();

  const actions: { icon: Icon; name: string; detail: string }[] = [
    {
      icon: CalendarIcon,
      name: t('Google Calendar'),
      detail: t('Checks availability and books meetings'),
    },
    {
      icon: CodeIcon,
      name: t('Custom actions'),
      detail: t('Calls your own APIs for orders, bookings and accounts'),
    },
    {
      icon: ClipboardListIcon,
      name: t('Forms'),
      detail: t('Collects details in a tidy form'),
    },
    {
      icon: IdentificationIcon,
      name: t('Collect data'),
      detail: t('Asks for a name, email or phone number when it matters'),
    },
    {
      icon: CursorClickIcon,
      name: t('Buttons'),
      detail: t('Links to checkout, pricing or a booking page'),
    },
    {
      icon: MailIcon,
      name: t('Email'),
      detail: t('Sends an email when something happens'),
    },
    {
      icon: SearchIcon,
      name: t('Web search'),
      detail: t('Looks up live information on the web'),
    },
    {
      icon: ChartBarIcon,
      name: t('Code interpreter'),
      detail: t('Runs calculations and draws charts'),
    },
  ];

  return (
    <section id='actions' className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:items-center lg:gap-12 lg:px-8'>
        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'order-2 lg:order-1 lg:col-span-6')}
        >
          <Stage
            variant='lavender'
            halftoneAt='100% 100%'
            className='px-4 py-8 sm:px-12 sm:py-14'
          >
            <p className='sr-only'>
              {t(
                'Example: a customer asks to move a salon appointment, and the agent looks up the booking, checks the calendar, moves it and emails a confirmation.',
              )}
            </p>
            <div aria-hidden='true'>
              <ActionsDemo />
            </div>
          </Stage>
        </div>

        <div className='order-1 lg:order-2 lg:col-span-6 lg:ps-6'>
          <SectionHeading
            size='md'
            title={t('It doesn’t stop at answers.')}
            tail={t('It gets the job done.')}
            lead={t(
              'Give your agent actions and it books appointments, looks things up in your systems, collects details and sends emails, right in the middle of the conversation.',
            )}
          />
          <ul className='mt-10 grid gap-x-8 gap-y-6 sm:grid-cols-2'>
            {actions.map(({ icon: Icon, name, detail }) => (
              <li key={name} className='flex gap-3.5'>
                <span
                  className={clsxm(
                    'flex h-9 w-9 flex-none items-center justify-center rounded-xl',
                    tone.surface,
                    tone.line,
                  )}
                >
                  <Icon className='h-[18px] w-[18px] text-primary-600 dark:text-primary-400' />
                </span>
                <span>
                  <span
                    className={clsxm(
                      'block text-[15px] font-semibold',
                      tone.ink,
                    )}
                  >
                    {name}
                  </span>
                  <span
                    className={clsxm(
                      'mt-0.5 block text-sm leading-6',
                      tone.body,
                    )}
                  >
                    {detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p className={clsxm('mt-8 text-sm', tone.quiet)}>
            {t('Actions are included in every paid plan.')}
          </p>
        </div>
      </div>
    </section>
  );
}

function ActionsDemo() {
  const { t } = useLocale('chatbots');
  const steps: { icon: Icon; title: string; via: string }[] = [
    { icon: CodeIcon, title: t('Found your booking'), via: t('Custom action') },
    {
      icon: CalendarIcon,
      title: t('Thursday at 4:00 is free'),
      via: t('Google Calendar'),
    },
    {
      icon: CalendarIcon,
      title: t('Booking moved'),
      via: t('Google Calendar'),
    },
    { icon: MailIcon, title: t('Confirmation sent'), via: t('Email') },
  ];

  return (
    <div
      className={clsxm(
        'mx-auto max-w-md overflow-hidden rounded-[22px] shadow-[0_40px_90px_-35px_rgba(30,20,90,0.45)]',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center gap-3 border-b border-black/[0.06] px-5 py-4 dark:border-white/[0.06]'>
        <span
          className={clsxm(
            'flex h-9 w-9 flex-none items-center justify-center rounded-full text-sm font-semibold text-white',
            SALON_COLOR,
          )}
        >
          S
        </span>
        <div>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            Studio Nine
          </p>
          <p className={clsxm('text-xs', tone.quiet)}>{t('Hair salon')}</p>
        </div>
      </div>

      <div className='space-y-4 px-5 py-5 text-[14px] leading-relaxed'>
        <div
          className={clsxm(styles.rv, 'flex justify-end')}
          style={delay(150)}
        >
          <p
            className={clsxm(
              'max-w-[82%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-4 py-2.5 dark:bg-white/[0.07]',
              tone.ink,
            )}
          >
            {t('Can I move my haircut to Thursday at 4?')}
          </p>
        </div>

        <ol className='relative space-y-3 ps-1'>
          <span className='absolute bottom-3 start-[17px] top-3 w-px bg-[#0f0f28]/10 dark:bg-white/10' />
          {steps.map(({ icon: Icon, title, via }, index) => (
            <li
              key={title}
              className={clsxm(styles.rv, 'relative flex items-center gap-3')}
              style={delay(450 + index * 260)}
            >
              <span
                className={clsxm(
                  'relative flex h-7 w-7 flex-none items-center justify-center rounded-full',
                  tone.surface,
                  tone.line,
                )}
              >
                <Icon className={clsxm('h-3.5 w-3.5', tone.body)} />
              </span>
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'block truncate text-[13px] font-semibold',
                    tone.ink,
                  )}
                >
                  {title}
                </span>
                <span className={clsxm('block text-xs', tone.quiet)}>
                  {via}
                </span>
              </span>
              <CheckGlyph className='h-4 w-4 text-emerald-500' />
            </li>
          ))}
        </ol>

        <div className={styles.rv} style={delay(1600)}>
          <p
            className={clsxm(
              'max-w-[88%] rounded-2xl rounded-es-md px-4 py-2.5 text-white',
              SALON_COLOR,
            )}
          >
            {t(
              'Done! You’re booked for Thursday at 4:00 with Mia. The confirmation is in your inbox.',
            )}
          </p>
        </div>
      </div>
    </div>
  );
}
